import React from "react";
import { Box, Skeleton } from "@mui/material";

const DataGridSkeletonForDocumentation = ({
    showSidebar = true,
    showTabs = true,
    tabCount = 7,
    showSearch = true,
    columns = 5,
    rows = 10,
    showPagination = true,
}) => {
    return (
        <Box className="documentation-page" sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Title Bar */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    px: 3,
                    py: 2,
                    borderBottom: "1px solid rgba(0,0,0,0.12)",
                    backgroundColor: "rgba(255,255,255,0.95)",
                }}
            >
                <Skeleton 
                    variant="text" 
                    width={280} 
                    height={42} 
                    sx={{ fontSize: '1.5rem' }}
                />
            </Box>

            {/* Main Content */}
            <Box className="documentation-content" sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                
                {/* Left Sidebar */}
                {showSidebar && (
                    <>
                        <Box sx={{ 
                            width: 280, 
                            p: 2, 
                            borderRight: "1px solid rgba(0,0,0,0.12)", 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 2.5,
                            backgroundColor: "rgba(255,255,255,0.95)",
                            overflowY: 'auto'
                        }}>
                            {/* Machine Selection Section */}
                            <Box>
                                <Skeleton variant="text" width={140} height={24} sx={{ mb: 1.5 }} />
                                <Skeleton variant="rectangular" width="100%" height={44} sx={{ borderRadius: "6px" }} />
                            </Box>

                            {/* Divider */}
                            <Skeleton variant="rectangular" width="100%" height={1} sx={{ borderRadius: 0 }} />

                            {/* Favorites Section */}
                            <Box>
                                <Skeleton variant="text" width={100} height={24} sx={{ mb: 1.5 }} />
                                {Array.from({ length: 4 }).map((_, idx) => (
                                    <Box key={`fav-${idx}`} sx={{ display: 'flex', gap: 1.2, mb: 1.2, alignItems: 'center' }}>
                                        <Skeleton variant="circular" width={20} height={20} />
                                        <Skeleton variant="text" width={`${70 + idx * 10}%`} height={18} />
                                        <Skeleton variant="circular" width={16} height={16} sx={{ ml: 'auto' }} />
                                    </Box>
                                ))}
                            </Box>

                            {/* Divider */}
                            <Skeleton variant="rectangular" width="100%" height={1} sx={{ borderRadius: 0 }} />

                            {/* Recently Viewed Section */}
                            <Box>
                                <Skeleton variant="text" width={160} height={24} sx={{ mb: 1.5 }} />
                                {Array.from({ length: 4 }).map((_, idx) => (
                                    <Box key={`recent-${idx}`} sx={{ display: 'flex', gap: 1.2, mb: 1.2, alignItems: 'center' }}>
                                        <Skeleton variant="circular" width={20} height={20} />
                                        <Skeleton variant="text" width={`${65 + idx * 8}%`} height={18} />
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Vertical Divider */}
                        <Box sx={{ width: '1px', backgroundColor: 'rgba(0,0,0,0.12)' }} />
                    </>
                )}

                {/* Right Main Content */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', p: 2 }}>
                    
                    {/* Search Bar */}
                    {showSearch && (
                        <Box sx={{ mb: 1 }}>
                            <Skeleton 
                                variant="rectangular" 
                                width="60%" 
                                height={30} 
                                sx={{ borderRadius: "12px", maxWidth: '600px' }} 
                            />
                        </Box>
                    )}

                    {/* Category Tabs */}
                    {showTabs && (
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                mb: 1.5,
                                borderBottom: "2px solid rgba(0,0,0,0.08)",
                                pb: 1,
                            }}
                        >
                            {Array.from({ length: tabCount }).map((_, idx) => (
                                <Skeleton
                                    key={`tab-${idx}`}
                                    variant="text"
                                    width={idx === 0 ? 90 : idx === 1 ? 200 : 140}
                                    height={36}
                                    sx={{ fontWeight: 500 }}
                                />
                            ))}
                        </Box>
                    )}

                    {/* Main Data Table */}
                    <Box
                        sx={{
                            borderRadius: "12px",
                            backgroundColor: (theme) => theme.palette.background.paper,
                            border: "1px solid rgba(0,0,0,0.12)",
                            overflow: "hidden",
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Table Header */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                px: 3,
                                py: 2.5,
                                backgroundColor: "rgba(0,0,0,0.03)",
                                borderBottom: "2px solid rgba(0,0,0,0.12)",
                            }}
                        >
                            {/* Checkbox */}
                            <Box sx={{ width: 60, mr: 2 }}>
                                <Skeleton variant="rectangular" width={20} height={20} />
                            </Box>
                            
                            {/* Filename */}
                            <Box sx={{ flex: 2.8, pr: 2 }}>
                                <Skeleton variant="text" height={26} width="55%" sx={{ fontWeight: 600 }} />
                            </Box>

                            {/* Machine Number */}
                            <Box sx={{ flex: 1.6, pr: 2 }}>
                                <Skeleton variant="text" height={26} width="75%" sx={{ fontWeight: 600 }} />
                            </Box>

                            {/* Created Date */}
                            <Box sx={{ flex: 1.1, pr: 2 }}>
                                <Skeleton variant="text" height={26} width="85%" sx={{ fontWeight: 600 }} />
                            </Box>

                            {/* Modified Date */}
                            <Box sx={{ flex: 1.1 }}>
                                <Skeleton variant="text" height={26} width="85%" sx={{ fontWeight: 600 }} />
                            </Box>

                            {/* Actions */}
                            <Box sx={{ width: 140, textAlign: 'center' }}>
                                <Skeleton variant="text" height={26} width={70} sx={{ mx: 'auto', fontWeight: 600 }} />
                            </Box>
                        </Box>

                        {/* Table Body */}
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                            {Array.from({ length: rows }).map((_, rowIdx) => (
                                <Box
                                    key={`row-${rowIdx}`}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        px: 3,
                                        py: 2.2,
                                        borderBottom: rowIdx !== rows - 1
                                            ? "1px solid rgba(0,0,0,0.06)"
                                            : "none",
                                        '&:hover': {
                                            backgroundColor: "rgba(0,121,255,0.04)",
                                        }
                                    }}
                                >
                                    {/* Checkbox */}
                                    <Box sx={{ width: 60, mr: 2 }}>
                                        <Skeleton variant="rectangular" width={20} height={20} />
                                    </Box>

                                    {/* Filename with icon and badge */}
                                    <Box sx={{ flex: 2.8, pr: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                            <Skeleton variant="rectangular" width={28} height={28} sx={{ borderRadius: '4px' }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Skeleton
                                                    variant="text"
                                                    height={20}
                                                    width={`${70 - rowIdx * 1.5}%`}
                                                    sx={{ mb: 0.3 }}
                                                />
                                                <Skeleton
                                                    variant="text"
                                                    height={16}
                                                    width="40%"
                                                    sx={{ fontSize: '0.75rem', opacity: 0.7 }}
                                                />
                                            </Box>
                                            <Skeleton 
                                                variant="rectangular" 
                                                width={32} 
                                                height={20} 
                                                sx={{ borderRadius: "10px" }} 
                                            />
                                        </Box>
                                    </Box>

                                    {/* Machine Number */}
                                    <Box sx={{ flex: 1.6, pr: 2 }}>
                                        <Skeleton
                                            variant="rectangular"
                                            height={20}
                                            width={`${65 - rowIdx * 1}%`}
                                            sx={{ borderRadius: "4px" }}
                                        />
                                    </Box>

                                    {/* Created Date */}
                                    <Box sx={{ flex: 1.1, pr: 2 }}>
                                        <Skeleton
                                            variant="rectangular"
                                            height={20}
                                            width="75%"
                                            sx={{ borderRadius: "4px" }}
                                        />
                                    </Box>

                                    {/* Modified Date */}
                                    <Box sx={{ flex: 1.1 }}>
                                        <Skeleton
                                            variant="rectangular"
                                            height={20}
                                            width="75%"
                                            sx={{ borderRadius: "4px" }}
                                        />
                                    </Box>

                                    {/* Actions */}
                                    <Box sx={{ width: 140, display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                                        <Skeleton variant="circular" width={32} height={32} />
                                        <Skeleton variant="circular" width={32} height={32} />
                                        <Skeleton variant="circular" width={32} height={32} />
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Pagination Footer */}
                        {showPagination && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    px: 3,
                                    py: 2,
                                    borderTop: "1px solid rgba(0,0,0,0.12)",
                                    backgroundColor: "rgba(0,0,0,0.02)",
                                }}
                            >
                                <Skeleton variant="text" width={160} height={22} />
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Skeleton variant="text" width={120} height={22} />
                                    <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: "6px" }} />
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Skeleton variant="circular" width={36} height={36} />
                                        <Skeleton variant="circular" width={36} height={36} />
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default DataGridSkeletonForDocumentation;